import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog.tsx';
import { Button } from './button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog.tsx';
import { Input } from './input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select.tsx';

// App-wide replacement for window.confirm / window.prompt with shadcn modals.
//
//   const { confirm, prompt } = useConfirm();
//   if (!(await confirm({ title, description, destructive: true }))) return;
//   const pick = await prompt({ title, label, defaultValue, options });
//
// One <ConfirmProvider> at the app root renders a single AlertDialog (confirm)
// and a single Dialog (prompt); each call resolves a Promise.

export type ConfirmOptions = {
  title?: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Red confirm button; defaults to true (most confirms guard a delete). */
  destructive?: boolean;
};

export type PromptOption = string | { value: string; label?: React.ReactNode };

export type PromptOptions = {
  title?: string;
  label?: string;
  description?: React.ReactNode;
  /** When given, the prompt is a Select over these options instead of a text input. */
  options?: PromptOption[];
  confirmText?: string;
  defaultValue?: string;
};

type ConfirmApi = {
  confirm: (opts?: ConfirmOptions) => Promise<boolean>;
  prompt: (opts?: PromptOptions) => Promise<string | null>;
};

type ConfirmState = Required<Omit<ConfirmOptions, 'description'>> & { description: React.ReactNode };
type PromptState = {
  title: string;
  label: string;
  description: React.ReactNode;
  options: PromptOption[] | null;
  confirmText: string;
  value: string;
};

const ConfirmContext = React.createContext<ConfirmApi | null>(null);

const optionValue = (o: PromptOption) => (typeof o === 'string' ? o : o.value);
const optionLabel = (o: PromptOption) => (typeof o === 'string' ? o : (o.label ?? o.value));

export function useConfirm(): ConfirmApi {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children?: React.ReactNode }) {
  const [confirmState, setConfirmState] = React.useState<ConfirmState | null>(null);
  const [promptState, setPromptState] = React.useState<PromptState | null>(null);
  const resolver = React.useRef<((value: never) => void) | null>(null);

  const settle = (val: boolean | string | null) => {
    const r = resolver.current as ((v: typeof val) => void) | null;
    resolver.current = null;
    setConfirmState(null);
    setPromptState(null);
    r?.(val);
  };

  const confirm = React.useCallback(
    (opts: ConfirmOptions = {}) =>
      new Promise<boolean>((resolve) => {
        resolver.current = resolve as (value: never) => void;
        setConfirmState({
          title: opts.title || 'Are you sure?',
          description: opts.description || '',
          confirmText: opts.confirmText || (opts.destructive ? 'Delete' : 'Confirm'),
          cancelText: opts.cancelText || 'Cancel',
          destructive: opts.destructive ?? true,
        });
      }),
    [],
  );

  const prompt = React.useCallback(
    (opts: PromptOptions = {}) =>
      new Promise<string | null>((resolve) => {
        resolver.current = resolve as (value: never) => void;
        const first = opts.options?.[0];
        setPromptState({
          title: opts.title || 'Enter a value',
          label: opts.label || '',
          description: opts.description || '',
          options: opts.options || null,
          confirmText: opts.confirmText || 'OK',
          value: opts.defaultValue ?? (first !== undefined ? optionValue(first) : ''),
        });
      }),
    [],
  );

  const api = React.useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <ConfirmContext.Provider value={api}>
      {children}

      <AlertDialog
        open={!!confirmState}
        onOpenChange={(o) => {
          if (!o) settle(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            {confirmState?.description ? (
              <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>{confirmState?.cancelText}</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                onClick={() => settle(true)}
                className={confirmState?.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
              >
                {confirmState?.confirmText}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!promptState}
        onOpenChange={(o) => {
          if (!o) settle(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{promptState?.title}</DialogTitle>
            {promptState?.description ? (
              <DialogDescription>{promptState.description}</DialogDescription>
            ) : null}
          </DialogHeader>
          {promptState &&
            (promptState.options ? (
              <Select
                value={String(promptState.value ?? '')}
                onValueChange={(v) => setPromptState((s) => (s ? { ...s, value: v } : s))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={promptState.label || 'Select…'} />
                </SelectTrigger>
                <SelectContent>
                  {promptState.options.map((o) => (
                    <SelectItem key={optionValue(o)} value={String(optionValue(o))}>
                      {optionLabel(o)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                autoFocus
                value={promptState.value ?? ''}
                placeholder={promptState.label || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setPromptState((s) => (s ? { ...s, value } : s));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') settle(promptState.value);
                }}
              />
            ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => settle(null)}>
              Cancel
            </Button>
            <Button onClick={() => settle(promptState?.value ?? null)}>{promptState?.confirmText}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
